<script setup>
  import { ref, onMounted, computed } from "vue";
  import * as XLSX from "xlsx";
  import ArgonButton from "@/components/ArgonButton.vue";
  import ArgonInput from "@/components/ArgonInput.vue";
  import api from "@/api/axios";
  // =======================
  // FILTROS
  // =======================
  const filtros = ref({
    fechaInicio: "",
    fechaFin: "",
    usuario: "",
    tipo: ""
  });

  // =======================
  // ESTADO VACÍO
  // =======================
  const reportes = ref([]);
  const cargando = ref(false);
  const generado = ref(false);

  const totalCotizaciones = computed(() => {
    return reportes.value.reduce(
      (total, item) => total + Number(item.total_cotizaciones || 0),
      0
    );
  });

  // =======================
  const generarReporte = async () => {

    generado.value = true;
    cargando.value = true;

    try {

      const res = await api.get("/quotes/reports", {
        params: {
          tipo: filtros.value.tipo,
          usuario: filtros.value.usuario || null,
          fechaInicio: filtros.value.fechaInicio,
          fechaFin: filtros.value.fechaFin
        }
      });


      reportes.value = res.data.data;


    } catch (error) {

      console.error(
        "Error generando reporte:",
        error.response?.data || error
      );

      reportes.value = [];

    } finally {

      cargando.value = false;

    }

  };

  const DESTINOS = {
    PUJ: "Punta Cana",
    CUN: "Cancun",
    GIG: "Brasil",
    Peru: "Peru",
    AUA: "Aruba",
    CUR: "Curazao",
    MBJ: "Jamaica",
    PTY: "Panama Playa",
    PTYC: "Panama Ciudad",
    VRA: "Varadero",
    HAV: "Habana",
    PCM: "Playa Del Carmen",
    MEX: "Ciudad de México",
    JPN: "Japon",
    "JPN GUIA": "Japon Con Guia",
    SJD: "Los Cabos",
    CTG: "Cartagena",
    SMR: "Santa Marta",
    ADZ: "San Andrés",
    BKK: "Tailandia",
    "PTY COMBINADO": "Panama Combinado",
    "HAV COMBINADO": "Habana Combinado",
    "MEX COMBINADO": "México Combinado",
    DECAMERON: "Decameron"
  };

  const exportar = async () => {

    try {

      const res = await api.get("/quotes/reports", {
        params: {
          tipo: filtros.value.tipo,
          usuario: filtros.value.usuario || null,
          fechaInicio: filtros.value.fechaInicio,
          fechaFin: filtros.value.fechaFin,
          exportar: true
        }
      });

      const datos = res.data.data.map(r => ({
        Fecha: r.fecha,
        Usuario: r.usuario,
        Destino: DESTINOS[r.destino] || r.destino,
      }));

      const worksheet = XLSX.utils.json_to_sheet(datos);

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "Reporte"
      );

      XLSX.writeFile(
        workbook,
        `Reporte_${filtros.value.tipo}_${new Date().toISOString().split("T")[0]}.xlsx`
      );

    } catch (error) {

      console.error(error);

    }

  };

  const usuarios = ref([]);

  const cargarUsuarios = async () => {
    try {

      const res = await api.get("/users/list");

      usuarios.value = res.data.data;

    } catch (error) {

      console.error(error);

    }
  };

  onMounted(() => {
    cargarUsuarios();
  });
</script>
<template>
  <div class="container-fluid py-4">

    <!-- HEADER -->
    <div class="d-flex justify-content-between align-items-center mb-4">

      <h4>
        <i class="bi bi-bar-chart-line-fill me-2"></i>
        Reportes
      </h4>

      <argon-button color="success" @click="exportar">
        <i class="bi bi-download me-1"></i>
        Exportar
      </argon-button>

    </div>


    <!-- FILTROS -->
    <div class="card mb-4">

      <div class="card-header">
        <strong>Filtros</strong>
      </div>


      <div class="card-body row g-3">


        <div class="col-md-3">

          <label>
            Fecha inicio
          </label>

          <argon-input type="date" v-model="filtros.fechaInicio" />

        </div>



        <div class="col-md-3">

          <label>
            Fecha fin
          </label>

          <argon-input type="date" v-model="filtros.fechaFin" />

        </div>



        <div class="col-md-3">

          <label>
            Usuario
          </label>

          <select v-model="filtros.usuario" class="form-control">
            <option value="">
              Todos los usuarios
            </option>

            <option v-for="usuario in usuarios" :key="usuario.id" :value="usuario.id">
              {{ usuario.name }}
            </option>
          </select>

        </div>



        <div class="col-md-3">

          <label>
            Reporte
          </label>


          <select v-model="filtros.tipo" class="form-control">

            <option value="destinos">
              Destinos
            </option>


            <option value="usuarios">
              Usuarios
            </option>


          </select>

        </div>



        <div class="col-12 text-end">


          <argon-button color="primary" @click="generarReporte">

            <i class="bi bi-search me-1"></i>

            Generar reporte

          </argon-button>


        </div>


      </div>

    </div>




    <!-- LOADING -->
    <div v-if="cargando" class="card text-center py-5">

      <div class="card-body">

        <div class="spinner-border text-primary"></div>


        <p class="mt-3 mb-0">
          Generando reporte...
        </p>


      </div>

    </div>





    <!-- SIN RESULTADOS -->
    <div v-else-if="generado && reportes.length === 0" class="card text-center py-5">

      <div class="card-body">


        <i class="bi bi-inbox fs-1 text-muted"></i>


        <h5 class="mt-3">
          Sin resultados
        </h5>


        <p class="text-sm text-muted">
          No hay datos para los filtros seleccionados
        </p>


      </div>


    </div>







    <!-- RESULTADOS -->
    <div v-else-if="reportes.length > 0" class="card">


      <div class="card-header">

        <strong>
          Resultados
        </strong>

      </div>





      <div class="table-responsive">


        <table class="table table-striped mb-0">



          <!-- REPORTE DESTINOS -->

          <thead v-if="filtros.tipo === 'destinos'">
            <tr>
              <th class="text-center">
                Destino
              </th>

              <th class="text-center">
                Total cotizaciones
              </th>
            </tr>
          </thead>





          <!-- REPORTE USUARIOS -->

          <thead v-if="filtros.tipo === 'usuarios'">

            <tr>

              <th class="text-center">
                Usuario
              </th>

              <th class="text-center">
                Total cotizaciones
              </th>

            </tr>

          </thead>








          <tbody v-if="filtros.tipo === 'destinos'">

            <tr v-for="(r, index) in reportes" :key="index">

              <td class="text-center">
                {{ DESTINOS[r.destino] }}
              </td>

              <td class="text-center">
                {{ r.total_cotizaciones }}
              </td>

            </tr>

          </tbody>







          <tbody v-if="filtros.tipo === 'usuarios'">

            <tr v-for="(r, index) in reportes" :key="index">

              <td class="text-center">
                {{ r.usuario }}
              </td>

              <td class="text-center">
                {{ r.total_cotizaciones }}
              </td>

            </tr>

          </tbody>


          <tfoot v-if="reportes.length">
            <tr class="table-primary fw-bold">

              <td class="text-center">
                TOTAL
              </td>

              <td class="text-center">
                {{ totalCotizaciones }}
              </td>

            </tr>
          </tfoot>
        </table>


      </div>


    </div>


  </div>
</template>